import {
  JobWithAddedIds,
  TrackingEventWithId,
} from '../internalModels';
import {
  CargoSummary,
  Container,
  RiskLevel,
  ShippingMode,
  Status,
  Stop,
  StopType,
  StopStatus,
  JobType,
  ReferenceNumber,
} from './dtos';
import { getConfiguration } from '@chr/common-web-ui-configuration';

const getHeaders = (turnstileResponse?: string): HeadersInit => ({
  'Content-Type': 'application/json',
  ...(turnstileResponse ? { 'X-Captcha-Token': turnstileResponse } : {}),
  ...(window.location.host.includes('passive-') || window.location.host.includes('local') ? { 'X-IsPassive': 'true' } : {}),
});

export const getShipmentDetails = async (
  chrTrackingNumber: string,
  turnstileResponse?: string,
  signal?: AbortSignal
) => {
  const { gatewayApiUri } = await getConfiguration<AppConfig>();

  const response = await fetch(
    `${gatewayApiUri}/trackings/${chrTrackingNumber}`,
    {
      headers: getHeaders(turnstileResponse),
      method: 'GET',
      signal,
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`failed to get external shipment: ${response.statusText}`, {
      cause: response.status,
    });
  }

  return response.json();
};

export const getExternalJob = async (
  identifier: string,
  postalCode: string,
  turnstileResponse?: string,
  signal?: AbortSignal
) => {
  const { gatewayApiUri } = await getConfiguration<AppConfig>();

  const response = await fetch(
    `${gatewayApiUri}/trackings/search?identifier=${identifier}&zipCode=${postalCode}`,
    {
      headers: getHeaders(turnstileResponse),
      method: 'GET',
      signal,
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`failed to get external job: ${response.statusText}`, {
      cause: response.status,
    });
  }

  return response.json();
};

export const getMockedShipmentDetails = async (
  chrTrackingNumber: string,
  jobType: JobType = JobType.Shipment,
  referenceNumbers: ReferenceNumber[] = [],
  signal?: AbortSignal
) => {
  let mode: string;
  let modeCode: string;

  switch (chrTrackingNumber) {
    case 'mockedAir':
      mode = ShippingMode.Air;
      modeCode = 'A';
      break;
    case 'mockedParcel':
      mode = ShippingMode.SmallParcel;
      modeCode = 'P';
      break;
    case 'mockedOcean':
    default:
      mode = ShippingMode.Ocean;
      modeCode = 'S';
      break;
  }

  return {
    cargoSummary: cargoSummary,
    carrierName: 'Carrier Name',
    chrTrackingNumber: chrTrackingNumber,
    matchedField: '',
    matchedValue: '',
    containers: [],
    currentLocation: {
      lastUpdateDateTime: '2024-04-03T15:15:41.886Z',
      location: {
        city: 'Louisville',
        country: 'USA',
        latitude: 0,
        longitude: 0,
        state: 'KY',
      },
    },
    currentMilestone: {
      location: {
        city: 'Louisville',
        country: 'USA',
        latitude: 0,
        longitude: 0,
        state: 'KY',
      },
    },
    estimatedArrival: '2024-04-04T15:15:41.886Z',
    estimatedDestinationDelivery: '04/05/2024',
    estimatedFirstDelivery: '04/06/2024',
    mode: mode,
    modeCode: modeCode,
    riskLevel: RiskLevel.OnTime,
    status: Status.InTransit,
    trackingEvents: [
      trackingEvent,
      trackingEvent,
      trackingEvent,
      trackingEvent,
      trackingEvent,
      trackingEvent,
      trackingEvent,
      trackingEventBefore,
      trackingEvent,
      trackingEvent,
      trackingEventAfter,
      trackingEvent,
      trackingEvent,
      trackingEvent,
      trackingEvent,
    ],
    stops: [...stopsWithSingleDropStop],
    jobType: jobType,
    referenceNumbers: referenceNumbers,
    items: [
      {
        id: 'item-1',
        sku: 'SKU-123',
        unitQuantity: 10,
        packaging: {
          value: 'Box'
        },
        totalPallets: 2,
        totalWeight: {
          unitRDN: 10,
          value: 100,
        },
      }
    ]
  } as JobWithAddedIds;
};

const trackingEvent: TrackingEventWithId = {
  id: '1',
  containerNumber: '123',
  eventDate: '2024-04-03T15:16:41.886Z',
  city: 'Des Moines',
  countryCode: 'USA',
  stateCode: 'IA',
  containerId: 0,
  notes:
    'Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes Notes',
  updatedDateTime: '2024-04-03T15:16:41.886Z',
};

const trackingEventBefore: TrackingEventWithId = {
  id: '2',
  containerNumber: '123',
  eventDate: '2024-04-01T11:43:41.886Z',
  city: 'Fort Dodge',
  countryCode: 'USA',
  stateCode: 'IA',
  containerId: 0,
  notes: 'Notes',
  updatedDateTime: '2024-04-01T11:43:41.886Z',
};

const trackingEventAfter: TrackingEventWithId = {
  id: '3',
  containerNumber: '123',
  eventDate: '2024-04-09T11:43:41.886Z',
  city: 'Ames',
  countryCode: 'USA',
  stateCode: 'IA',
  containerId: 0,
  notes: 'Notes',
  updatedDateTime: '2024-04-09T11:43:41.886Z',
};

const stopsWithSingleDropStop: Stop[] = [
  {
    arrived: '2022-10-10T10:30:00.000Z',
    departed: '2022-10-10T11:30:00.000Z',
    eta: '2022-10-10T10:30:00.000Z',
    etaSource: 'CARRIER',
    location: {
      name: 'Somewhere House',
      city: 'Sioux Falls',
      country: 'USA',
      latitude: 1,
      longitude: 1,
      state: 'SD',
      zipcode: '57103',
    },
    numberOfItems: 1,
    pallets: 1,
    quantity: 1,
    requested: '2022-10-10T10:30:00.000Z',
    scheduledOpenDateTime: '2022-10-10T10:30:00.000T',
    scheduledCloseDateTime: '2022-10-10T12:30:00.000T',
    sequenceNumber: 1,
    stopType: StopType.Pick,
    stopStatus: StopStatus.Completed,
    totalWeight: {
      metric: 0,
      standard: 1,
    },
  },
  {
    location: {
      city: 'St. Peter',
      country: 'CN',
      latitude: 2,
      longitude: 2,
      state: 'CN',
      zipcode: '56082',
    },
    numberOfItems: 2,
    pallets: 2,
    quantity: 2,
    requested: '2022-10-10T14:30:00.000Z',
    scheduledOpenDateTime: '2022-10-10T14:30:00.000Z',
    scheduledCloseDateTime: '2022-10-10T15:30:00.000Z',
    sequenceNumber: 2,
    stopType: StopType.Pick,
    stopStatus: StopStatus.InTransit,
    totalWeight: {
      metric: 2,
      standard: 2,
    },
  },
  {
    location: {
      city: 'Minneapolis',
      country: 'USA',
      latitude: 3,
      longitude: 3,
      state: 'MN',
      zipcode: '55407',
    },
    numberOfItems: 3,
    pallets: 3,
    quantity: 3,
    requested: '2022-10-10T18:00:00.000Z',
    scheduledOpenDateTime: '2022-10-10T18:00:00.000Z',
    scheduledCloseDateTime: '2022-10-10T19:00:00.000Z',
    sequenceNumber: 3,
    stopType: StopType.Drop,
    stopStatus: StopStatus.None,
    totalWeight: {
      metric: 3,
      standard: 3,
    },
  },
];

const stopsWithMultipleDropStop: Stop[] = [
  ...stopsWithSingleDropStop,
  {
    location: {
      city: 'St. Paul',
      country: 'USA',
      latitude: 2,
      longitude: 3,
      state: 'MN',
      zipcode: '55101',
    },
    numberOfItems: 1,
    pallets: 1,
    quantity: 1,
    requested: '2022-10-12T18:00:00.000Z',
    scheduledOpenDateTime: '2022-10-12T18:00:00.000Z',
    scheduledCloseDateTime: '2022-10-12T19:00:00.000Z',
    sequenceNumber: 4,
    stopType: StopType.Drop,
    stopStatus: StopStatus.None,
    totalWeight: {
      metric: 3,
      standard: 3,
    },
  },
];

const cargoSummary: CargoSummary = {
  arrived: '01/09/2024',
  flight: 'Flight',
  flightNumber: 'Flight Number',
  customes: 'Customes',
  departed: '01/10/2024',
  outerPackQuantity: 1,
  actualWeight: {
    metric: 2,
    standard: 1,
  },
  chargeableWeight: {
    metric: 2,
    standard: 1,
  },
  uom: 'CTN',
};

const container: Container = {
  containerNumber: '123',
  containerType: "40' HIGH CUBE CONTAINER",
  estimatedDelivery: '01/11/2024',
  delivered: '01/11/2024',
  eventDateTime: '01/12/2024',
  location: {
    city: 'City',
    country: 'Country',
    latitude: 0,
    longitude: 0,
    state: 'State',
  },
  numberOfCommodities: 1,
  outerPackQuantity: 1,
  priority: true,
  riskLevel: RiskLevel.AtRisk,
  status: Status.InTransit,
  sealNumber: 'Seal Number',
  volume: { metric: 0, standard: 1 },
  uom: 'UOM',
};

const container2: Container = {
  containerNumber: '456',
  containerType: 'Pallet',
  estimatedDelivery: '2024-01-22',
  delivered: '2024-01-22',
  eventDateTime: '2024-01-12',
  location: {
    city: 'Another City',
    country: 'Another Country',
    latitude: 0,
    longitude: 0,
    state: 'Another State',
  },
  numberOfCommodities: 1,
  outerPackQuantity: 1,
  priority: true,
  riskLevel: RiskLevel.OnTime,
  status: Status.Delivered,
  sealNumber: 'Seal Number',
  totalWeight: { metric: 3, standard: 3 },
  volume: { metric: 0, standard: 2170 },
  uom: 'UOM',
};

const container3: Container = {
  containerNumber: '789',
  containerType: 'Pallet',
  estimatedDelivery: '2024-01-22',
  delivered: '2024-01-22',
  eventDateTime: '2024-01-12',
  location: {
    city: 'Another City',
    country: 'Another Country',
    latitude: 0,
    longitude: 0,
    state: 'Another State',
  },
  numberOfCommodities: 1,
  outerPackQuantity: 1,
  priority: true,
  riskLevel: RiskLevel.OnTime,
  status: Status.InTransit,
  sealNumber: 'Seal Number',
  totalWeight: { metric: 3, standard: 3 },
  volume: { metric: 0, standard: 2170 },
  uom: 'UOM',
};

const container4: Container = {
  containerNumber: '1011',
  containerType: 'Box',
  estimatedDelivery: '2024-01-22',
  delivered: '',
  eventDateTime: '2024-01-12',
  location: {
    city: 'Another City',
    country: 'Another Country',
    latitude: 0,
    longitude: 0,
    state: 'Another State',
  },
  numberOfCommodities: 1,
  outerPackQuantity: 1,
  priority: true,
  riskLevel: RiskLevel.OnTime,
  status: Status.Delivered,
  sealNumber: 'Seal Number',
  totalWeight: { metric: 3, standard: 3 },
  uom: 'UOM',
};

export const getMockedShipmentDetailsWithContainers = async (
  chrTrackingNumber: string,
  includeMoreContainers?: boolean,
  singleDropStop?: boolean,
  signal?: AbortSignal
) => {
  const containers =
    includeMoreContainers === true
      ? [container, container2, container3, container4]
      : [container, container2];
  const stops =
    singleDropStop === true
      ? stopsWithSingleDropStop
      : stopsWithMultipleDropStop;
  const shipment: JobWithAddedIds = {
    cargoSummary: cargoSummary,
    carrierName: 'Carrier Name',
    chrTrackingNumber: chrTrackingNumber,
    matchedField: '',
    matchedValue: '',
    containers,
    currentLocation: {
      lastUpdateDateTime: '2024-04-03T15:15:41.886Z',
      location: {
        city: 'Louisville',
        country: 'USA',
        latitude: 0,
        longitude: 0,
        state: 'KY',
      },
    },
    currentMilestone: {
      location: {
        city: 'Louisville',
        country: 'USA',
        latitude: 0,
        longitude: 0,
        state: 'KY',
      },
    },
    estimatedArrival: '2024-04-04T15:15:41.886Z',
    estimatedDestinationDelivery: '04/05/2024',
    estimatedFirstDelivery: '04/06/2024',
    mode: ShippingMode.Ocean,
    modeCode: 'S',
    riskLevel: RiskLevel.OnTime,
    status: Status.InTransit,
    trackingEvents: [trackingEvent],
    stops: stops,
    items: [
      {
        id: 'item-1',
        sku: 'SKU-123',
        packaging: {
          value: 'Box'
        },
        unitQuantity: 15,
        totalPallets: 2.0,
        totalWeight: {
          unitRDN: 10,
          value: 150,
        },
      }
    ]
  };

  // make a deep copy to ensure any changes made don't change values
  return JSON.parse(JSON.stringify(shipment)) as JobWithAddedIds;
};

export const getMockedShipmentDetailsWithDeliveredContainers = async (
  chrTrackingNumber: string,
  includeMoreContainers?: boolean,
  singleDropStop?: boolean,
  signal?: AbortSignal
) => {
  const shipment = await getMockedShipmentDetailsWithContainers(
    chrTrackingNumber,
    includeMoreContainers,
    singleDropStop,
    signal
  );
  shipment.containers = [
    ...shipment.containers.filter((c) => c.status === Status.Delivered),
  ];
  shipment.stops
    .filter((s) => s.stopType === StopType.Drop)
    .forEach((s) => (s.arrived = '2022-10-12'));
  shipment.status = Status.Delivered;

  return shipment;
};
